import {useState, useEffect, useCallback, useRef} from 'react';
import {View, Text, StyleSheet, TouchableOpacity} from 'react-native';
import Alert from "@/lib/Alert";
import {VideoView, useVideoPlayer} from 'expo-video';
import {AppDataMediaItem} from "@/data/app-data-interfaces";
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {ThemeType} from "@/theming/baseTheme";
import {getVideoUrl} from "@/data/app-data-api";
import {PlayButton} from "@/components/common/PlayButton";
import {LoadSpinner} from "@/components/common/LoadSpinner";
import {formatDuration} from "@/lib/utils";
import {useAddLastViewed} from "@/data/user-data-hooks";
import {Image} from "expo-image";
import {Pause} from 'lucide-react-native';

interface AudioPlayerProps {
    audio: AppDataMediaItem;
    orgId: string;
}

export const AudioPlayer = ({orgId, audio}: AudioPlayerProps) => {
    const {theme} = useCurrentTheme();
    const styles = createStyles(theme);
    const [isLoading, setIsLoading] = useState(false);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(audio.duration || 0);
    const addLastViewed = useAddLastViewed();
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const player = useVideoPlayer(audioUrl ? {uri: audioUrl} : null, (player) => {
        player.loop = false;
        player.play();
    });

    // Track playback state and time
    useEffect(() => {
        if (!player) return;

        const statusSub = player.addListener('statusChange', (payload) => {
            if (payload.status === 'readyToPlay') {
                const dur = player.duration;
                setDuration(isFinite(dur) && dur > 0 ? dur : audio.duration || 0);
            }
        });

        const playingSub = player.addListener('playingChange', (payload) => {
            setIsPlaying(payload.isPlaying);
        });

        return () => {
            statusSub.remove();
            playingSub.remove();
        };
    }, [player, audio.duration]);

    // Poll current time while playing
    useEffect(() => {
        if (isPlaying && player) {
            timerRef.current = setInterval(() => {
                const time = player.currentTime;
                setCurrentTime(isFinite(time) ? time : 0);
            }, 250);
        } else {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        }
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [isPlaying, player]);

    const handlePlayPress = useCallback(async () => {
        addLastViewed.mutate({orgId, videoId: audio.id});

        if (audioUrl && player) {
            if (isPlaying) {
                player.pause();
            } else {
                if (duration > 0 && currentTime >= duration - 0.5) {
                    player.currentTime = 0;
                    setCurrentTime(0);
                }
                player.play();
            }
            return;
        }

        setIsLoading(true);
        try {
            const url = await getVideoUrl(audio.id);
            setAudioUrl(url);
        } catch (error) {
            console.error('Error loading audio:', error);
            Alert.alert('Error', 'Failed to load audio. Please try again.', [{text: 'OK'}]);
        } finally {
            setIsLoading(false);
        }
    }, [audioUrl, player, isPlaying, audio.id, orgId, addLastViewed, currentTime, duration]);

    // Auto-play after VideoView mounts (needed on web where play() requires a mounted video element)
    useEffect(() => {
        if (audioUrl && player) {
            player.play();
        }
    }, [audioUrl, player]);

    const handleSeek = useCallback((locationX: number, layoutWidth: number) => {
        if (!player || !duration || !isFinite(duration)) return;
        const ratio = Math.max(0, Math.min(1, locationX / layoutWidth));
        const seekTime = ratio * duration;
        if (!isFinite(seekTime)) return;
        player.currentTime = seekTime;
        setCurrentTime(seekTime);
    }, [player, duration]);

    const progress = duration > 0 ? currentTime / duration : 0;

    return (
        <View style={styles.container}>
            {/* Hidden VideoView required for expo-video playback on web */}
            {audioUrl && player && (
                <VideoView player={player} style={styles.hiddenPlayer}/>
            )}

            {/* Thumbnail - always visible */}
            <Image
                source={audio.image.lg}
                style={styles.thumbnail}
                placeholder={{blurhash: audio.imageBlurhash}}
                contentFit="cover"
                transition={200}
            />

            {/* Play/Pause overlay */}
            <TouchableOpacity
                style={styles.overlay}
                onPress={handlePlayPress}
                disabled={isLoading}
                activeOpacity={0.8}
            >
                {isLoading ? (
                    <LoadSpinner size={80}/>
                ) : isPlaying ? (
                    <View style={styles.pauseButton}>
                        <Pause size={36} color={theme.colors.primary} fill={theme.colors.surface}/>
                    </View>
                ) : (
                    <PlayButton size={80} onPress={handlePlayPress}/>
                )}
            </TouchableOpacity>

            {/* Bottom controls bar */}
            <View style={styles.controlsBar}>
                <Text style={styles.timeText}>{formatDuration(currentTime)}</Text>
                <TouchableOpacity
                    style={styles.progressBarContainer}
                    activeOpacity={1}
                    onPress={(e) => {
                        const {locationX} = e.nativeEvent;
                        e.currentTarget.measure((_x, _y, width) => {
                            handleSeek(locationX, width);
                        });
                    }}
                >
                    <View style={styles.progressBarBackground}>
                        <View style={[styles.progressBarFill, {width: `${progress * 100}%`}]}/>
                    </View>
                </TouchableOpacity>
                <Text style={styles.timeText}>{formatDuration(duration)}</Text>
            </View>
        </View>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    container: {
        width: '100%',
        height: 250,
        backgroundColor: '#000',
        position: 'relative',
    },
    thumbnail: {
        width: '100%',
        height: '100%',
        position: 'absolute',
    },
    overlay: {
        ...StyleSheet.absoluteFillObject,
        justifyContent: 'center',
        alignItems: 'center',
        paddingBottom: 40,
    },
    pauseButton: {
        width: 80,
        height: 80,
        borderRadius: 40,
        justifyContent: 'center',
        alignItems: 'center',
    },
    controlsBar: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 12,
        paddingVertical: 8,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        gap: 8,
    },
    timeText: {
        color: '#fff',
        fontSize: 12,
        fontWeight: '600',
        minWidth: 40,
        textAlign: 'center',
    },
    progressBarContainer: {
        flex: 1,
        height: 20,
        justifyContent: 'center',
    },
    progressBarBackground: {
        height: 4,
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        borderRadius: 2,
        overflow: 'hidden',
    },
    progressBarFill: {
        height: '100%',
        backgroundColor: theme.colors.primary,
        borderRadius: 2,
    },
    hiddenPlayer: {
        width: 0,
        height: 0,
        position: 'absolute',
    },
});
